/**
 * MODALE: SendBulkEmailModal
 * Modale pour envoyer un email de masse avec prévisualisation et sélection des destinataires
 */

import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { sendEmailSchema, type SendEmailInput } from '@/schemas/email';
import { Modal, ModalFooter } from '../ui/modal';
import { Form } from '../ui/form';
import { FormInput, RichTextEditor, Checkbox } from '../ui';
import { EmailTemplateSelector } from '../email/EmailTemplateSelector';
import { useState, useEffect, useMemo } from 'react';
import { AlertCircle, Mail, User } from 'lucide-react';
import type { Contact } from '@/types/contact';
import type { FormSubmission } from '@/types/form';
import type { Crew } from '@/types/crew';
import type { EmailTemplate } from '@/types/emailTemplate';
import { useSupabase } from '@/hooks/useSupabase';
import { useEmailTemplates } from '@/hooks/useEmailTemplates';
import { isTemplateVisibleFor } from '@/lib/emailTags';
import { toast } from 'sonner';

// V4 R8 — détection contenu RichTextEditor non vide (markup résiduel <p></p>)
function isBodyEmpty(body: string): boolean {
  if (!body) return true;
  const stripped = body.replace(/<p>\s*<\/p>/g, '').trim();
  return stripped.length === 0;
}

type BulkEntity = Contact | FormSubmission | Crew;
type EntityType = 'contact' | 'prospect' | 'crew';

interface Recipient {
  entityId: string;
  entityType: EntityType;
  email: string;
  name: string;
  selected: boolean;
}

interface SendBulkEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  entities: BulkEntity[];
  entityType: EntityType;
  onSuccess?: () => void;
}

export function SendBulkEmailModal({
  isOpen,
  onClose,
  entities,
  entityType,
  onSuccess,
}: SendBulkEmailModalProps) {
  const supabase = useSupabase();
  const { templates: allTemplates } = useEmailTemplates();
  const emailTemplates = allTemplates.filter((t) =>
    isTemplateVisibleFor(t.entity_scope, entityType)
  );
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplateId, setSelectedTemplateId] = useState<string>('');
  const [sendingProgress, setSendingProgress] = useState<{ current: number; total: number } | null>(
    null
  );

  const form = useForm<SendEmailInput>({
    resolver: zodResolver(sendEmailSchema),
    defaultValues: {
      to: [],
      cc: [],
      subject: '',
      body: '',
    },
  });

  // Charger les destinataires selon le type d'entité
  useEffect(() => {
    async function loadRecipients() {
      if (!isOpen || entities.length === 0) {
        setRecipients([]);
        setLoading(false);
        return;
      }

      setLoading(true);
      const recipientsList: Recipient[] = [];

      try {
        if (entityType === 'contact') {
          // Pour les contacts, récupérer directement l'email
          (entities as Contact[]).forEach((contact) => {
            if (contact.email) {
              recipientsList.push({
                entityId: contact.id,
                entityType: 'contact',
                email: contact.email,
                name: `${contact.first_name} ${contact.last_name}`,
                selected: true,
              });
            }
          });
        } else if (entityType === 'prospect') {
          // Pour les prospects, récupérer depuis contact
          (entities as FormSubmission[]).forEach((prospect) => {
            if (prospect.contact?.email) {
              recipientsList.push({
                entityId: prospect.id,
                entityType: 'prospect',
                email: prospect.contact.email,
                name:
                  `${prospect.contact.first_name || ''} ${prospect.contact.last_name || ''}`.trim() ||
                  prospect.contact.email,
                selected: true,
              });
            }
          });
        } else if (entityType === 'crew') {
          // Pour les équipages, récupérer tous les membres
          const crewIds = (entities as Crew[]).map((c) => c.id);
          const { data: crewMembers, error } = await supabase
            .from('crew_members')
            .select('crew_id, contact:contacts(id, email, first_name, last_name)')
            .in('crew_id', crewIds);

          if (error) {
            console.error('Erreur lors du chargement des membres:', error);
            toast.error('Erreur lors du chargement des membres');
          } else if (crewMembers) {
            crewMembers.forEach((member: any) => {
              const contact = member.contact;
              if (contact?.email) {
                // Vérifier si ce contact n'est pas déjà dans la liste (peut être membre de plusieurs équipages)
                const exists = recipientsList.some((r) => r.email === contact.email);
                if (!exists) {
                  recipientsList.push({
                    entityId: member.crew_id,
                    entityType: 'crew',
                    email: contact.email,
                    name:
                      `${contact.first_name || ''} ${contact.last_name || ''}`.trim() ||
                      contact.email,
                    selected: true,
                  });
                }
              }
            });
          }
        }

        setRecipients(recipientsList);
      } catch (error) {
        console.error('Erreur lors du chargement des destinataires:', error);
        toast.error('Erreur lors du chargement des destinataires');
      } finally {
        setLoading(false);
      }
    }

    loadRecipients();
  }, [isOpen, entities, entityType, supabase]);

  // Mettre à jour le champ 'to' du formulaire quand les destinataires sélectionnés changent
  useEffect(() => {
    const selectedEmails = recipients.filter((r) => r.selected).map((r) => r.email);
    form.setValue('to', selectedEmails, { shouldValidate: true });
  }, [recipients, form]);

  const selectedCount = useMemo(() => recipients.filter((r) => r.selected).length, [recipients]);

  const excludedCount = useMemo(
    () => entities.length - recipients.length,
    [entities.length, recipients.length]
  );

  const handleClose = () => {
    form.reset({ to: [], cc: [], subject: '', body: '' });
    setRecipients([]);
    setSelectedTemplateId('');
    onClose();
  };

  // V4 R8 — appliquer un modèle (préremplit sujet + corps).
  // Confirme avant d'écraser le contenu déjà saisi par l'utilisateur.
  const handleApplyTemplate = (template: EmailTemplate | null) => {
    if (!template) {
      setSelectedTemplateId('');
      return;
    }
    const currentSubject = form.getValues('subject') || '';
    const currentBody = form.getValues('body') || '';
    const hasUserContent = currentSubject.trim().length > 0 || !isBodyEmpty(currentBody);
    if (hasUserContent) {
      const ok = window.confirm(
        'Appliquer ce modèle remplacera le sujet et le corps actuels. Continuer ?'
      );
      if (!ok) return;
    }
    setSelectedTemplateId(template.id);
    form.setValue('subject', template.subject, { shouldValidate: true, shouldDirty: true });
    form.setValue('body', template.body, { shouldValidate: true, shouldDirty: true });
  };

  const toggleRecipient = (email: string) => {
    setRecipients((prev) =>
      prev.map((r) => (r.email === email ? { ...r, selected: !r.selected } : r))
    );
  };

  const toggleAll = () => {
    const allSelected = recipients.every((r) => r.selected);
    setRecipients((prev) => prev.map((r) => ({ ...r, selected: !allSelected })));
  };

  const handleFormSubmit = async (data: SendEmailInput) => {
    const selectedRecipients = recipients.filter((r) => r.selected);
    if (selectedRecipients.length === 0) {
      toast.error('Aucun destinataire sélectionné');
      return;
    }

    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      if (!session) throw new Error('Non authentifié');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const total = selectedRecipients.length;
      let successCount = 0;
      const errors: string[] = [];

      setSendingProgress({ current: 0, total });

      for (let i = 0; i < selectedRecipients.length; i++) {
        const recipient = selectedRecipients[i];
        try {
          const response = await fetch(`${supabaseUrl}/functions/v1/send-email`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Authorization: `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              to: [recipient.email],
              cc: data.cc,
              subject: data.subject,
              body: data.body,
              entity_type: recipient.entityType,
              entity_id: recipient.entityId,
              is_bulk: true,
            }),
          });

          if (!response.ok) {
            const errorData = await response.json().catch(() => ({}));
            errors.push(
              `${recipient.email} : ${(errorData as any).error ?? `HTTP ${response.status}`}`
            );
          } else {
            successCount++;
          }
        } catch (err) {
          errors.push(
            `${recipient.email} : ${err instanceof Error ? err.message : 'Erreur inconnue'}`
          );
        }

        setSendingProgress({ current: i + 1, total });
      }

      setSendingProgress(null);

      if (errors.length === 0) {
        toast.success('Emails envoyés', {
          description: `${successCount} email(s) envoyé(s) avec succès`,
        });
        if (onSuccess) onSuccess();
        handleClose();
      } else if (successCount > 0) {
        toast.warning(`${successCount} envoyé(s), ${errors.length} échec(s)`, {
          description: errors.slice(0, 3).join(' — '),
        });
        if (onSuccess) onSuccess();
        handleClose();
      } else {
        toast.error("Aucun email n'a pu être envoyé", {
          description: errors[0],
        });
      }
    } catch (error) {
      setSendingProgress(null);
      console.error("Erreur lors de l'envoi des emails:", error);
      toast.error('Erreur', {
        description: error instanceof Error ? error.message : "Impossible d'envoyer les emails",
      });
    }
  };

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Envoi d'email en masse" size="2xl">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-zinc-900" />
        </div>
      </Modal>
    );
  }

  if (recipients.length === 0) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Envoi d'email en masse" size="2xl">
        <div className="flex flex-col items-center justify-center py-12 gap-4">
          <AlertCircle className="h-12 w-12 text-amber-500" />
          <div className="text-center">
            <p className="text-lg font-medium text-zinc-900">Aucun destinataire valide</p>
            <p className="text-sm text-zinc-600 mt-1">
              {excludedCount > 0
                ? `${excludedCount} entité(s) sans adresse email ont été exclues`
                : "Aucune entité sélectionnée ne possède d'adresse email"}
            </p>
          </div>
          <button
            onClick={handleClose}
            className="px-4 py-2 bg-zinc-900 text-white rounded-md hover:bg-zinc-800"
          >
            Fermer
          </button>
        </div>
      </Modal>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="Envoi d'email en masse"
      size="2xl"
      footer={
        <ModalFooter
          onCancel={handleClose}
          submitLabel={
            sendingProgress
              ? `Envoi en cours (${sendingProgress.current}/${sendingProgress.total})…`
              : `Envoyer à ${selectedCount} destinataire(s)`
          }
          isSubmitting={form.formState.isSubmitting || sendingProgress !== null}
          formId="send-bulk-email-form"
        />
      }
    >
      <Form {...form}>
        <form id="send-bulk-email-form" onSubmit={form.handleSubmit(handleFormSubmit)}>
          <div className="flex flex-col gap-4">
            {/* Alerte si des entités ont été exclues */}
            {excludedCount > 0 && (
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                <AlertCircle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-amber-800">
                  <strong>{excludedCount}</strong> entité(s) sans adresse email ont été exclues
                  automatiquement.
                </p>
              </div>
            )}

            {/* Liste des destinataires */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-zinc-700">
                  Destinataires ({selectedCount} sélectionné{selectedCount > 1 ? 's' : ''})
                </label>
                <button
                  type="button"
                  onClick={toggleAll}
                  className="text-sm text-zinc-600 hover:text-zinc-900 underline"
                >
                  {recipients.every((r) => r.selected)
                    ? 'Tout désélectionner'
                    : 'Tout sélectionner'}
                </button>
              </div>

              <div className="max-h-48 overflow-y-auto border border-zinc-200 rounded-md">
                {recipients.map((recipient) => (
                  <div
                    key={recipient.email}
                    className="flex items-center gap-3 p-3 hover:bg-zinc-50 border-b border-zinc-100 last:border-b-0"
                  >
                    <Checkbox
                      checked={recipient.selected}
                      onChange={() => toggleRecipient(recipient.email)}
                    />
                    <div
                      className="flex items-center gap-2 flex-1 min-w-0 cursor-pointer"
                      onClick={() => toggleRecipient(recipient.email)}
                    >
                      <User className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-zinc-900 truncate">
                          {recipient.name}
                        </p>
                        <p className="text-xs text-zinc-600 truncate">{recipient.email}</p>
                      </div>
                    </div>
                    <Mail className="h-4 w-4 text-zinc-400 flex-shrink-0" />
                  </div>
                ))}
              </div>

              {form.formState.errors.to && (
                <p className="text-sm text-red-600 mt-1">{form.formState.errors.to.message}</p>
              )}
            </div>

            {/* Sélecteur de modèle (V4 R8) */}
            <EmailTemplateSelector
              value={selectedTemplateId}
              templates={emailTemplates}
              onChange={handleApplyTemplate}
              selectId="send-bulk-email-template-select"
            />

            {/* Sujet */}
            <FormInput
              control={form.control as any}
              name="subject"
              label="Sujet"
              required
              placeholder="Objet de l'email"
            />

            {/* Message avec éditeur riche */}
            <div>
              <label className="block text-sm font-medium text-zinc-700 mb-2">
                Message <span className="text-red-500">*</span>
              </label>
              <Controller
                name="body"
                control={form.control}
                render={({ field, fieldState }) => (
                  <RichTextEditor
                    value={field.value}
                    onChange={field.onChange}
                    placeholder="Rédigez votre message..."
                    minHeight="300px"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>
          </div>
        </form>
      </Form>
    </Modal>
  );
}
